import type { LanguageTag } from "@growthwise/core";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  CATALOGS,
  DEFAULT_LANGUAGE,
  HTML_LANG,
  LANGUAGE_STORAGE_KEY,
  isSupportedLanguage,
  localizedPath,
  splitLocalePath,
  translate,
  translateList,
  type Messages,
} from "./index";

interface LanguageContextValue {
  language: LanguageTag;
  messages: Messages;
  /** Translate a dot-separated key, with optional `{placeholder}` values. */
  t: (key: string, values?: Record<string, string | number>) => string;
  /** Translate a key whose value is an array of strings. */
  tList: (key: string) => string[];
  /** Build an in-app URL for the current language. */
  path: (to: string) => string;
  /** Build an in-app URL for a specific language. */
  pathFor: (language: LanguageTag, to: string) => string;
  /** Switch language, staying on the equivalent page. */
  switchLanguage: (next: LanguageTag) => void;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const rawLang = params["lang"];
  const languageValid = isSupportedLanguage(rawLang);
  const language: LanguageTag = languageValid ? rawLang : DEFAULT_LANGUAGE;

  const messages = CATALOGS[language];
  const unsupportedLang =
    typeof rawLang === "string" && rawLang.length > 0 && !languageValid;

  /**
   * Keeps the document in step with the active language.
   *
   * `lang` matters for screen-reader pronunciation and for the CJK font and
   * line-height overrides in tokens.css, so it is set on every change rather
   * than only at first render.
   */
  useEffect(() => {
    if (unsupportedLang) return;
    document.documentElement.lang = HTML_LANG[language];
  }, [language, unsupportedLang]);

  /**
   * Remembers the choice.
   *
   * Written whenever the active language changes -- including when it comes
   * from the URL -- so that a visitor who shares a `/zh-CN/` link and later
   * lands on `/` gets Chinese without having to choose again.
   */
  useEffect(() => {
    if (unsupportedLang) return;
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    } catch {
      // Private browsing or a storage quota error must not break navigation.
    }
  }, [language, unsupportedLang]);

  const switchLanguage = useCallback(
    (next: LanguageTag) => {
      if (next === language) return;

      // Stay on the same page: only the language segment changes, and the
      // query string and hash are preserved. In-progress form state survives
      // because this is a client-side navigation within the same React tree,
      // and forms keep their draft in component state or in a saved draft.
      const { rest } = splitLocalePath(location.pathname);
      const target = `${localizedPath(next, rest)}${location.search}${location.hash}`;

      try {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
      } catch {
        // Ignored, as above.
      }

      navigate(target, { replace: false });
    },
    [language, location.hash, location.pathname, location.search, navigate],
  );

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      messages,
      t: (key, values) => translate(messages, key, values),
      tList: (key) => translateList(messages, key),
      path: (to) => localizedPath(language, to),
      pathFor: (lang, to) => localizedPath(lang, to),
      switchLanguage,
    }),
    [language, messages, switchLanguage],
  );

  if (unsupportedLang && typeof rawLang === "string") {
    const escaped = rawLang.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const rest =
      location.pathname.replace(new RegExp(`^/${escaped}(?=/|$)`), "") || "/";
    return (
      <Navigate
        to={`${localizedPath(DEFAULT_LANGUAGE, rest)}${location.search}${location.hash}`}
        replace
      />
    );
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextValue => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used inside a LanguageProvider");
  }
  return context;
};

/** Convenience hook for components that only need the translate function. */
export const useT = (): LanguageContextValue["t"] => useLanguage().t;
